import { useCallback, useEffect } from 'react'
import { UseFormMethods, useWatch } from 'react-hook-form'
import { Maybe } from 'types/graphqlTypes'
import { TCreateShipmentSchema } from '../../../../schema'
import { TStopSchema } from '../../../../schema/ShipmentSchema/StopSchema'

export interface IUseFirstPickupValuesProps
  extends Pick<UseFormMethods<TCreateShipmentSchema>, 'control' | 'setValue'> {
  numberOfStops: number
}

const useFirstPickupValues = ({ control, numberOfStops, setValue }: IUseFirstPickupValuesProps) => {
  const firstPickup = useWatch<TStopSchema>({ control, name: 'shipment.stops[0]' })
  const {
    billOfLadingReference: firstBOL,
    commodity: firstCommodityDescription,
    palletCount: firstPalletCount,
    poReference: firstPO,
    weight: firstWeight,
  } = firstPickup || {}

  /**
   * Sets a value for other stops except first PU
   */
  const setStopValues = useCallback(
    <T extends keyof TStopSchema>(fieldName: T, value: Maybe<string | number>) => {
      for (let i = 1; i < numberOfStops; i += 1) {
        setValue(`shipment.stops[${i}].${fieldName}`, value)
      }
    },
    [numberOfStops, setValue]
  )

  /**
   * When first PU weight changes - set the same value for other stops and total weight
   */
  useEffect(() => {
    setStopValues('weight', firstWeight)
    setValue('shipment.totalWeight', firstWeight)
  }, [firstWeight, setStopValues, setValue])

  /**
   * When first PU pallet count changes - set the same value for other stops and total pallets
   */
  useEffect(() => {
    setStopValues('palletCount', firstPalletCount)
    setValue('shipment.totalPallets', firstPalletCount)
  }, [firstPalletCount, setStopValues, setValue])

  /**
   * When first PU commodity description changes - set the same value for other stops
   */
  useEffect(() => {
    setStopValues('commodity', firstCommodityDescription)
  }, [firstCommodityDescription, setStopValues])

  /**
   * When first PU PO changes - set the same value for other stops
   */
  useEffect(() => {
    setStopValues('poReference', firstPO)
  }, [firstPO, setStopValues])

  /**
   * When first PU BOL changes - set the same value for other stops
   */
  useEffect(() => {
    setStopValues('billOfLadingReference', firstBOL)
  }, [firstBOL, setStopValues])
}

export default useFirstPickupValues
